import ArrowSVG from '@public/icons/arr.svg';
import ArrSVG from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { AnimatePresence, Variants, m as motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { STORAGE_SCROLL_POSITION } from '@/consts';
import { PostCategory } from '@/entities';
import { ApiNewsRequest, AppNewsResponse, useNewsQuery } from '@/shared/api';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import {
    useIsPageTransitioningValue,
    usePageTransitionValue,
    useSetPageTransitionState,
} from '@/shared/atoms/page-transition';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import SubscribePopup, { SUBSCRIBE_POPUP_NAME } from '@/shared/components/popups/SubscribePopup/SubscribePopup';
import Breadcrumbs, { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import Button from '@/shared/components/shared/Button';
import Dropdown from '@/shared/components/shared/Dropdown';
import Image from '@/shared/components/shared/Image';
import Link from '@/shared/components/shared/Link';
import ListPagination from '@/shared/components/shared/ListPagination';
import Loader from '@/shared/components/shared/Loader';
import Responsive from '@/shared/components/shared/Responsive';
import Reveal from '@/shared/components/shared/Reveal';
import { scrollToHref } from '@/shared/components/shared/ScrollToLink/ScrollToLink';
import SearchInput from '@/shared/components/shared/SearchInput';
import Typografed from '@/shared/components/shared/Typografed';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { usePreserveScrollY } from '@/shared/hooks/use-preserve-scroll-y';
import { usePrevious } from '@/shared/hooks/use-previous';
import { CommonPageProps } from '@/shared/lib/types';
import { easeOutQuart } from '@/shared/lib/utils/easings';
import { chpu } from '@/shared/lib/utils/filter/chpu';
import { encodeUrl } from '@/shared/lib/utils/filter/chpu/encode-url';
import getNoun from '@/shared/lib/utils/get-noun';

export type NewsPageViewProps = {
    meta: CommonPageProps['meta'];
    breadcrumbs: AppBreadcrumbs;
    h1: string;
    categorySlug: string | null;
    subscribeText: string;
    description: string;
    initialNewsResponse: AppNewsResponse;
    initialParams: ApiNewsRequest;
    categories: PostCategory[];
    mappedCategories: {
        name: string;
    }[];
    filter: PostCategory[];
};

type Props = NewsPageViewProps;

export const pageLimits = [8, 16, 24];
const DEFAULT_PAGE_LIMIT = pageLimits[0];

const variants: Variants = {
    hidden: {
        y: 10,
        opacity: 0,
    },
    visible: {
        y: 0,
        opacity: 1,
    },
};

const NewsPageView = ({
    meta,
    h1,
    categorySlug,
    subscribeText,
    breadcrumbs,
    description,
    initialNewsResponse,
    categories,
    filter,
    initialParams = {},
}: Props) => {
    const ref = useRef(null);
    const router = useRouter();
    const [params, setParams] = useState<ApiNewsRequest>(() => {
        if (typeof window !== 'undefined') {
            router.replace(
                {
                    pathname: router.pathname,
                    query: { ...router.query, ...initialParams },
                },
                encodeUrl(router.asPath, initialParams, {
                    ignoredKeys: ['categories', ...(initialParams.limit === DEFAULT_PAGE_LIMIT ? ['limit'] : [])],
                }),
                { shallow: true, scroll: false },
            );
        }

        const category = categories.find((category) => category.slug === categorySlug);

        return { ...initialParams, categories: category?.id ? [category.id] : [] };
    });

    const pageTransition = usePageTransitionValue();
    const isPageTransitioning = useIsPageTransitioningValue();
    const { openPopup } = useSetOpenedPopupsState();
    const [searchTerm, setSearchTerm] = useState(params.q || '');
    const debouncedSearchTerm = useDebounce(searchTerm.trim(), 500);
    const prevDebouncedSearchTerm = usePrevious(debouncedSearchTerm);
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const selectedFilter = categorySlug ? categories.find((category) => category.slug === categorySlug)?.name : 'Все';
    const setPageTransition = useSetPageTransitionState();

    usePreserveScrollY('/news/');

    const { data, isFetching, error } = useNewsQuery(
        {
            params,
            postCategories: categories,
        },
        initialNewsResponse,
    );

    const scrollToOrigin = () => scrollToHref('#news-list');

    const onPaginationChange = (page: number) => {
        setParams((prevParams) => {
            const newParams = { ...prevParams };

            if (page === 1) {
                delete newParams.page;
            } else {
                newParams.page = page;
            }

            const transformedParams = {
                ...newParams,
                categories: (newParams.categories || []).map(
                    (id) => categories.find((category) => category.id === id)?.slug || '',
                ),
            };

            router.push(
                {
                    pathname: router.pathname,
                    query: { ...router.query, ...transformedParams },
                },
                encodeUrl(
                    transformedParams.categories && transformedParams.categories.length > 0
                        ? `/news/${categories.find((category) => category.slug === transformedParams.categories[0])?.slug}/`
                        : '/news/',
                    transformedParams,
                    {
                        ignoredKeys: [
                            'categories',
                            ...(transformedParams.limit === DEFAULT_PAGE_LIMIT ? ['limit'] : []),
                        ],
                    },
                ),
                { shallow: true, scroll: false },
            );

            requestAnimationFrame(scrollToOrigin);

            return newParams;
        });
    };

    const onPageLimitChange = (limit: number) => {
        setParams((prevParams) => {
            const newParams = { ...prevParams, limit };
            delete newParams.page;

            const transformedParams = {
                ...newParams,
                categories: (newParams.categories || []).map(
                    (id) => categories.find((category) => category.id === id)?.slug || '',
                ),
            };

            router.replace(
                {
                    pathname: router.pathname,
                    query: { ...router.query, ...transformedParams },
                },
                encodeUrl(
                    transformedParams.categories && transformedParams.categories.length > 0
                        ? `/news/${categories.find((category) => category.slug === transformedParams.categories[0])?.slug}/`
                        : '/news/',
                    transformedParams,
                    {
                        ignoredKeys: [
                            'categories',
                            ...(transformedParams.limit === DEFAULT_PAGE_LIMIT ? ['limit'] : []),
                        ],
                    },
                ),
                { shallow: true, scroll: false },
            );

            return newParams;
        });
    };

    const onPick = useCallback(() => {
        sessionStorage.setItem(STORAGE_SCROLL_POSITION, window.scrollY.toString());
        setPageTransition({ mode: 'wait', name: 'leaveNewsToNews' });
    }, [setPageTransition]);

    const handleSearchInput = useCallback((e: any) => {
        setSearchTerm(e.target.value);
    }, []);

    const news = data?.data?.items ?? [];

    /**
     * Возврат назад по истории
     */
    useEffect(() => {
        const onRouteChange = (nextPagePathname: string) => {
            if (nextPagePathname.replace(/\/filter\/.*/, '') !== router.pathname) {
                return;
            }

            const paramsFromUrl = chpu.parseUrlToParams(nextPagePathname);

            paramsFromUrl.limit =
                typeof paramsFromUrl.limit === 'string' ? parseInt(paramsFromUrl.limit) : paramsFromUrl.limit;

            if (typeof paramsFromUrl.limit === 'undefined') {
                paramsFromUrl.limit = DEFAULT_PAGE_LIMIT;
            }

            paramsFromUrl.page =
                typeof paramsFromUrl.page === 'string' ? parseInt(paramsFromUrl.page) : paramsFromUrl.page;

            if (!Object.is(params, paramsFromUrl)) {
                setParams((prevParams) => ({ ...prevParams, ...paramsFromUrl }));
            }
        };

        router.events.on('routeChangeStart', onRouteChange);

        return () => {
            router.events.off('routeChangeStart', onRouteChange);
        };
    }, [router.events, categories, params, router.pathname]);

    useEffect(() => {
        setParams((prevParams) => {
            const newParams = { ...prevParams };

            if (typeof prevDebouncedSearchTerm === 'string' && debouncedSearchTerm !== prevDebouncedSearchTerm) {
                delete newParams.page;
            }

            if (debouncedSearchTerm) {
                newParams.q = debouncedSearchTerm;
            } else {
                delete newParams.q;
            }

            const transformedParams = {
                ...newParams,
                categories: (newParams.categories || []).map(
                    (id) => categories.find((category) => category.id === id)?.slug || '',
                ),
            };

            router.push(
                {
                    pathname: router.pathname,
                    query: { ...router.query, ...transformedParams },
                },
                encodeUrl(
                    transformedParams.categories && transformedParams.categories.length > 0
                        ? `/news/${categories.find((category) => category.slug === transformedParams.categories[0])?.slug}/`
                        : '/news/',
                    transformedParams,
                    {
                        ignoredKeys: [
                            'categories',
                            ...(transformedParams.limit === DEFAULT_PAGE_LIMIT ? ['limit'] : []),
                        ],
                    },
                ),
                { shallow: true, scroll: false },
            );

            return newParams;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);

    return (
        <DefaultLayout ref={ref}>
            <Head>
                <title>
                    {[
                        [meta.title, typeof params.page === 'number' ? `Страница ${params.page}` : null]
                            .filter(Boolean)
                            .join(' — '),
                        meta.baseTitle,
                    ]
                        .filter(Boolean)
                        .join(' — ')}
                </title>
            </Head>
            <div className="news-page-top js-header-theme-trigger wrapper" data-theme="dark">
                <Breadcrumbs className="news-page-breadcrumbs" breadcrumbs={breadcrumbs} />
                <div className="news-page-title-block">
                    <h1 className="news-page-title h2">
                        <Typografed>{h1}</Typografed>
                    </h1>
                    <div className="news-page-description-block">
                        {description && (
                            <div className="news-page-description text-s">
                                <Typografed>{description}</Typografed>
                            </div>
                        )}
                        <Button
                            className="news-page-btn text-xs"
                            variant="outline-dark"
                            onClick={() => {
                                openPopup(SUBSCRIBE_POPUP_NAME);
                            }}
                        >
                            {subscribeText}
                        </Button>
                    </div>
                </div>
                <div className="news-top-block">
                    {!isDesktop ? (
                        <Dropdown>
                            <Dropdown.Toggler className="news-select-btn">
                                <span className="text-xs">{selectedFilter}</span>
                                <ArrowSVG className="news-arrow-svg" />
                            </Dropdown.Toggler>
                            <Dropdown.Content className="news-filter__content" position="bottom-right">
                                <ul className="list-unstyled menu-dropdown-list">
                                    {filter.map((item, i) => (
                                        <li key={i}>
                                            <Dropdown.Picker
                                                tag={Link}
                                                href={`/news/${item.slug ? `${item.slug}/` : ''}`}
                                                className={classNames('news-select-item text-xs', {
                                                    'is-active':
                                                        typeof item.id === 'number' &&
                                                        params.categories &&
                                                        (params.categories.length > 0
                                                            ? params.categories.includes(item.id)
                                                            : item.slug === ''),
                                                })}
                                                onClick={() => {
                                                    onPick();
                                                }}
                                            >
                                                {item.name}
                                            </Dropdown.Picker>
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown.Content>
                        </Dropdown>
                    ) : (
                        <div className="news-filters">
                            {filter.map((item, i) => (
                                <Link
                                    href={`/news/${item.slug ? `${item.slug}/` : ''}`}
                                    className={classNames('news-filter text-xs', {
                                        'is-active':
                                            params.categories &&
                                            (params.categories.length > 0
                                                ? params.categories.includes(Number(item.id))
                                                : item.slug === ''),
                                    })}
                                    key={i}
                                    onClick={() => {
                                        onPick();
                                    }}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    )}
                    <Dropdown className="page-limit-select">
                        <Dropdown.Toggler className="news-select-btn">
                            <div className="text-xs">{`показывать по: ${params.limit || DEFAULT_PAGE_LIMIT}`}</div>
                            <ArrowSVG className="news-arrow-svg" />
                        </Dropdown.Toggler>
                        <Dropdown.Content className="news-filter__content" position="bottom-right">
                            <ul className="list-unstyled menu-dropdown-list">
                                {pageLimits.map((limit) => (
                                    <li key={limit}>
                                        <Dropdown.Picker
                                            className={classNames('news-select-item text-xs', {
                                                'is-active': params.limit === limit,
                                            })}
                                            onClick={() => onPageLimitChange(limit)}
                                        >
                                            {limit} {getNoun(limit, 'карточка', 'карточки', 'карточек')}
                                        </Dropdown.Picker>
                                    </li>
                                ))}
                            </ul>
                        </Dropdown.Content>
                    </Dropdown>
                    <SearchInput value={searchTerm} onInput={handleSearchInput} placeholder="Поиск по новостям" />
                </div>
                {error ? <div className="text-l error-message news-list-error-message">{error.message}</div> : null}
                {news.length > 0 ? (
                    <ul id="news-list" className="list-unstyled news-page-list">
                        <AnimatePresence>
                            {news.map((item, i) => (
                                <motion.li
                                    key={`${i}_${item.slug}`}
                                    className="news-page-list-item"
                                    variants={variants}
                                    initial="hidden"
                                    animate={
                                        isFetching || (pageTransition.name === 'leaveNewsToNews' && isPageTransitioning)
                                            ? 'hidden'
                                            : 'visible'
                                    }
                                    exit="hidden"
                                    transition={{ duration: 0.5, ease: easeOutQuart }}
                                >
                                    <Link
                                        href={['/news', categorySlug || item.categories[0]?.slug || '', item.slug]
                                            .filter(Boolean)
                                            .join('/')}
                                        className="news-item"
                                    >
                                        <Responsive className="news-list-item-img-wrapper">
                                            {item.img?.src && (
                                                <Image
                                                    className="news-list-item-img"
                                                    src={item.img.src}
                                                    alt={item.img.alt || item.title || ''}
                                                    title={item.img.title}
                                                    width={item.img.width}
                                                    height={item.img.height}
                                                    sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 25vw"
                                                    style={{ opacity: 0 }}
                                                    onLoad={(event) => {
                                                        if (event.target instanceof HTMLImageElement) {
                                                            event.target.style.opacity = '';
                                                            event.target.classList.add('fade-in');
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Responsive>
                                        <div className="news-list-item-date">
                                            <div className="text-xs">
                                                <Typografed>{item.date}</Typografed>
                                            </div>
                                            <div className="text-xs">
                                                <Typografed>
                                                    {item.categories.map((category) => category.name).join(' / ')}
                                                </Typografed>
                                            </div>
                                        </div>
                                        <div className="news-list-item-title text-s">
                                            <Typografed>{item.title}</Typografed>
                                        </div>
                                        <div className="news-list-item-btn">
                                            <ArrSVG />
                                        </div>
                                    </Link>
                                </motion.li>
                            ))}
                        </AnimatePresence>
                    </ul>
                ) : (
                    !isFetching && (
                        <Reveal>
                            <div className="text-l news-list-not-found">
                                <Typografed>
                                    {Object.values({ ...params, limit: null }).filter(
                                        (value) => Boolean(value) && (Array.isArray(value) ? value.length > 0 : true),
                                    ).length > 0
                                        ? 'По выбранным фильтрам ничего не найдено'
                                        : 'Новости не найдены'}
                                </Typografed>
                            </div>
                        </Reveal>
                    )
                )}
                {(data?.data?.totalCount ?? 0) > (params.limit || 0) && (
                    <Reveal>
                        <div className="filterable-blog-list__pagination">
                            <ListPagination
                                totalPages={Math.ceil((data?.data?.totalCount ?? 0) / (params.limit || 8))}
                                activePage={Number(params.page ?? 1)}
                                onClickHandler={onPaginationChange}
                                onClickPreventDefault
                                chpuIgnoredKeys={[
                                    'categorySlug',
                                    'categories',
                                    ...(params.limit === DEFAULT_PAGE_LIMIT ? ['limit'] : []),
                                ]}
                            />
                        </div>
                    </Reveal>
                )}
                <Loader isFetching={isFetching} />
            </div>
            <SubscribePopup />
        </DefaultLayout>
    );
};

export default NewsPageView;
